import React, {useState} from 'react';
import ButtonSolid from 'components/Buttons/ButtonSolid';
import {surveyInstance} from 'screens/InsomniaSurvey/InsomniaSurvey';
import {SurveyData} from 'screens/InsomniaSurvey/utils/surveyData';
import {MEDICARE_WAITLIST_COPY} from 'surveys/insomnia_funnel/survey/config';
import Analytics from 'utils/Analytics';
import styles from './SleepioRxInterstitials.module.css';

const RISE_APP_STORE_URL = 'https://apps.apple.com/us/app/rise-sleep-tracker/id1453884781';
// Long enough for Segment to flush, short enough that the tap still feels instant.
const TRACK_FLUSH_TIMEOUT_MS = 1000;

const isValidEmail = (email: string) => /\S+@\S+\.\S+/.test(email);

export const SleepioNotAvailableInterstitial: React.FC = () => {
  const reason = surveyInstance.getValue('sleepio_not_available_reason');
  const insuranceCoverage = surveyInstance.getValue('insurance_coverage');
  // Medicare patients get a waitlist instead of a plain dead end: we can't see
  // them yet, but want to reach them once we can.
  const isMedicare = insuranceCoverage === 'medicare';
  const [email, setEmail] = useState<string>(
    surveyInstance.getValue('clinic_waitlist_email') ?? surveyInstance.getValue('email') ?? '',
  );
  const [emailError, setEmailError] = useState('');
  const [isLeaving, setIsLeaving] = useState(false);

  const handleContinue = async () => {
    const trimmedEmail = email.trim();
    // The email is optional: an empty box continues, a malformed one doesn't.
    if (isMedicare && trimmedEmail && !isValidEmail(trimmedEmail)) {
      setEmailError('Enter a valid email address.');
      return;
    }

    setIsLeaving(true);
    const sends: Array<Promise<unknown> | undefined> = [
      Analytics.track('SleepioRx Not Available Continue Clicked', {
        reason,
        insurance_state: surveyInstance.getValue('insurance_state'),
        insurance_coverage: insuranceCoverage,
      }),
    ];

    if (isMedicare && trimmedEmail) {
      surveyInstance.setValue('clinic_waitlist_email', trimmedEmail);
      surveyInstance.setValue('email', trimmedEmail);
      SurveyData.save(surveyInstance);
      Analytics.identify({email: trimmedEmail, medicare_waitlist_joined: 'true'});
      // Same event as the /book-appointment waitlist, so one query covers both funnels.
      sends.push(
        Analytics.track('Clinic Web Intake Waitlist Submitted', {
          source_screen: 'sleepio_medicare_waitlist',
          has_email: true,
          insurance_state: surveyInstance.getValue('insurance_state'),
          insurance_coverage: insuranceCoverage,
          medicare_plan_type: surveyInstance.getValue('medicare_plan_type'),
        }),
      );
    }

    await Promise.race([
      Promise.allSettled(sends),
      new Promise(resolve => setTimeout(resolve, TRACK_FLUSH_TIMEOUT_MS)),
    ]);
    window.location.assign(RISE_APP_STORE_URL);
  };

  const title = isMedicare
    ? MEDICARE_WAITLIST_COPY.headline
    : reason === 'payer_ineligible'
      ? 'Not yet available through your plan.'
      : 'Not yet available in your state.';

  return (
    <div className={`${styles.container} ${styles.centered}`}>
      <div className={styles.heroCard}>
        <div className={styles.kicker}>SleepioRx</div>
        <h1 className={styles.title}>{title}</h1>
        <p className={styles.body}>
          {isMedicare
            ? MEDICARE_WAITLIST_COPY.body
            : 'SleepioRx is not currently available for this insurance and location combination. Keep using Rise as usual while we work on expanding access.'}
        </p>
        {isMedicare ? (
          <>
            <input
              className={styles.textInput}
              value={email}
              onChange={event => {
                setEmail(event.target.value);
                setEmailError('');
              }}
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="you@example.com"
              aria-label="Email address"
              aria-invalid={Boolean(emailError)}
            />
            {emailError ? <p className={styles.error}>{emailError}</p> : null}
          </>
        ) : null}
      </div>
      <div className={styles.footer}>
        <ButtonSolid title="Continue to Rise" variant="purple" onClick={handleContinue} disabled={isLeaving} />
      </div>
    </div>
  );
};

export default SleepioNotAvailableInterstitial;
